import { useQueryClient } from '@tanstack/react-query';
import { Form, Input, InputNumber, Select, Switch } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';

import { apiFetch } from '@/api/client';
import {
  categoryAttributesKey,
  itemCategoriesKey,
  useCategoryAttributes,
  useItemAttributes,
  type CategoryAttribute,
  type ItemCategory,
} from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { uploadCategoryPhoto } from '@/api/upload';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { useOpenAtTop } from '@/components/useOpenAtTop';
import { Button, Card, Notice } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { defaultSaleRateField } from '@core/catalogue/forms';
import { blankToNull, humanizeEnum } from '@core/format/text';
import { blockedReason } from '@core/session/capability';
import type { Session } from '@core/session/types';

import { isPhoto, parentChoices, shownPhoto, type PhotoChange } from './categoryForm';

interface CategoryFormValues {
  code: string;
  name: string;
  /** Create only — see the note on the switch below. */
  shared?: boolean;
  description?: string | null;
  parentId?: number | null;
  turnaroundBufferDays?: number | null;
  isAccessory: boolean;
  defaultHsnCode?: string | null;
  defaultSacCode?: string | null;
  defaultTaxRatePct?: number | null;
  defaultSaleTaxRatePct?: number | null;
  sortOrder: number;
  isActive: boolean;
}

/** What one Save did: the category as written, and the photograph's failure if it had one. */
interface Saved {
  category: ItemCategory;
  photoError: unknown;
}

/**
 * A category, new or existing, on a page of its own (spec Sections 7, 9, 14).
 *
 * <p>
 * <strong>A page, not a drawer</strong> — the rule settled on 2026-09-08 — and the template's own
 * add-product layout: what the category <em>is</em> on the left, its picture and its status on the
 * right. It replaces the drawer the list used to open.
 * </p>
 *
 * <p>
 * <strong>The photograph is chosen before saving, and written by Save.</strong> A new category has
 * no id until its insert has run, and the upload needs one, so the page holds the chosen file and
 * Save creates the category first, then sends the picture. If the category is written and the
 * picture is not — no bucket on this server, a dropped connection — the page stays open on the
 * category it just created, says which half failed, and Save tries the picture again. Closing would
 * throw the chosen photograph away with a success message over it.
 * </p>
 */
export function CategoryPage({
  session,
  category,
  categories,
  onClose,
}: {
  session: Session;
  /** The category being edited, or undefined for a new one. */
  category: ItemCategory | undefined;
  categories: ItemCategory[];
  onClose: () => void;
}) {
  const [form] = Form.useForm<CategoryFormValues>();
  const queryClient = useQueryClient();

  // Becomes the created category if Save wrote it but not its photograph, so the next Save edits
  // rather than creating a second one.
  const [existing, setExisting] = useState(category);
  const [photo, setPhoto] = useState<PhotoChange>({ kind: 'keep' });
  const [photoError, setPhotoError] = useState<unknown>(null);

  const isEdit = existing !== undefined;
  const fields = useAutoFocusFirstField<HTMLDivElement>();
  useOpenAtTop();

  const save = useApiMutation<Saved, CategoryFormValues>({
    mutationFn: async (raw) => {
      const { defaultSaleTaxRatePct, ...rest } = blankToNull(raw);
      const values = {
        ...rest,
        ...defaultSaleRateField(defaultSaleTaxRatePct ?? null, existing !== undefined),
      };

      let written = existing
        ? await apiFetch<ItemCategory>(`/api/item-category/${existing.id}`, {
            method: 'PUT',
            body: values,
          })
        : await apiFetch<ItemCategory>('/api/item-category', { method: 'POST', body: values });

      try {
        if (photo.kind === 'replace') {
          written = await uploadCategoryPhoto(written.id, photo.file);
        } else if (photo.kind === 'remove' && written.imageUrl !== null) {
          await apiFetch<void>(`/api/item-category/${written.id}/photo`, { method: 'DELETE' });
          written = { ...written, imageUrl: null };
        }
      } catch (error) {
        return { category: written, photoError: error };
      }

      return { category: written, photoError: null };
    },
    success: ({ photoError: failed }) =>
      failed
        ? 'Category saved. The photo was not — see the note beside it.'
        : isEdit
          ? 'Category updated.'
          : 'Category created.',
    invalidates: [itemCategoriesKey],
    onSuccess: ({ category: written, photoError: failed }) => {
      if (!failed) {
        onClose();

        return;
      }

      setExisting(written);
      setPhotoError(failed);
    },
  });

  // A local preview of the chosen file, released when another is chosen or the page closes —
  // an object URL holds the file in memory until it is revoked.
  const stagedUrl = useMemo(
    () => (photo.kind === 'replace' ? URL.createObjectURL(photo.file) : null),
    [photo],
  );

  useEffect(
    () => () => {
      if (stagedUrl) {
        URL.revokeObjectURL(stagedUrl);
      }
    },
    [stagedUrl],
  );

  const picture = shownPhoto(photo, stagedUrl, existing?.imageUrl ?? null);

  // The photograph is written through the category's edit grant, so a role that may create
  // categories but not edit them is told so here rather than after its category has been saved.
  const photoBlocked = blockedReason(session, 'ItemCategory:Edit');

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            aria-label="Back to categories"
            className="text-default flex size-8 cursor-pointer items-center justify-center rounded hover:bg-light hover:text-gray-900"
          >
            <i className="icon icon-arrow-left" />
          </button>
          <div>
            <h1 className="page-title">{existing ? `Edit ${existing.name}` : 'New category'}</h1>
            <p className="page-subtitle">
              What kind of thing an item is. Drives cleaning turnaround, package slots and damage
              rates.
            </p>
          </div>
        </div>
      </div>

      {/*
        No <form>: the attributes card below carries its own controls, and a nested form is invalid
        HTML whose buttons submit the outer one. Enter navigation comes from `enterKeyScope` on the
        grid instead, and the last field hands focus to the save button.
      */}
      <Form<CategoryFormValues>
        form={form}
        component={false}
        layout="vertical"
        requiredMark={false}
        initialValues={
          existing ?? {
            code: '',
            name: '',
            shared: false,
            isAccessory: false,
            sortOrder: 0,
            isActive: true,
          }
        }
        onFinish={(values) => void save.mutateAsync(values).catch(() => undefined)}
      >
        <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
          {/* ----------------------------------------------------- left column */}
          <div className="col-span-12 flex flex-col gap-4 lg:col-span-8">
            <Card>
              <SectionTitle>Basic information</SectionTitle>

              <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
                <Form.Item
                  name="code"
                  label="Code"
                  rules={[{ required: true, message: 'A code is required' }]}
                  extra={
                    isEdit
                      ? 'Fixed at creation — package slots and imports refer to it'
                      : 'Upper case, e.g. SHERWANI. Fixed once created.'
                  }
                >
                  <Input disabled={isEdit} className="font-mono-nums" />
                </Form.Item>

                <Form.Item
                  name="name"
                  label="Name"
                  rules={[{ required: true, message: 'A name is required' }]}
                >
                  <Input />
                </Form.Item>
              </div>

              {/*
                Create only. Moving a category between the two scopes is not an edit — narrowing a
                shared one hides it at every other branch, where it may already have children
                pointing at it — so the server fixes scope at creation and the page does not offer
                to change it.
              */}
              {!isEdit && (
                <Form.Item
                  name="shared"
                  label="Shared across all branches"
                  valuePropName="checked"
                  extra="On: every branch sees this category. Off: only the branch you are working at. Sharing needs a company-wide role."
                >
                  <Switch size="small" />
                </Form.Item>
              )}

              <Form.Item
                name="parentId"
                label="Sits under"
                extra="Sub-categories inherit the turnaround buffer unless they set their own."
              >
                <Select
                  options={parentChoices(categories, existing)}
                  allowClear
                  placeholder="Nothing — this is a top-level category"
                />
              </Form.Item>

              <Form.Item name="description" label="Description" className="mb-0">
                <Input.TextArea rows={2} placeholder="What belongs in this category." />
              </Form.Item>
            </Card>

            <Card>
              <SectionTitle>Turnaround and tax</SectionTitle>

              <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-2">
                <Form.Item
                  name="turnaroundBufferDays"
                  label="Turnaround buffer"
                  extra="Cleaning days before a returned garment is bookable again. Leave empty to inherit."
                >
                  <InputNumber min={0} precision={0} addonAfter="days" className="w-full" />
                </Form.Item>

                <Form.Item name="sortOrder" label="Sort order">
                  <InputNumber precision={0} className="w-full" />
                </Form.Item>
              </div>

              <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-4">
                <Form.Item
                  name="defaultHsnCode"
                  label="Default HSN"
                  extra="Goods code, for a sale."
                  className="mb-0"
                >
                  <Input className="font-mono-nums" />
                </Form.Item>

                <Form.Item
                  name="defaultSacCode"
                  label="Default SAC"
                  extra="Service code, for a rental."
                  className="mb-0"
                >
                  <Input className="font-mono-nums" />
                </Form.Item>

                <Form.Item
                  name="defaultTaxRatePct"
                  label="Rental tax"
                  extra="On a hire."
                  className="mb-0"
                >
                  <InputNumber min={0} max={100} precision={2} addonAfter="%" className="w-full" />
                </Form.Item>

                <Form.Item
                  name="defaultSaleTaxRatePct"
                  label="Sale tax"
                  extra="When a garment here is sold."
                  className="mb-0"
                >
                  <InputNumber min={0} max={100} precision={2} addonAfter="%" className="w-full" />
                </Form.Item>
              </div>
            </Card>

            {/*
              Edit only: an assignment points at a category id, and an IDENTITY key does not exist
              until the insert has run.
            */}
            {existing && (
              <Card>
                <CategoryAttributesSection session={session} category={existing} />
              </Card>
            )}
          </div>

          {/* ---------------------------------------------------- right column */}
          <div className="col-span-12 flex flex-col gap-4 lg:col-span-4">
            <Card>
              <SectionTitle>Photo</SectionTitle>

              <PhotoPicker
                picture={picture}
                blocked={photoBlocked}
                onPick={(file) => {
                  setPhoto({ kind: 'replace', file });
                  setPhotoError(null);
                }}
                onRemove={() =>
                  setPhoto(existing?.imageUrl ? { kind: 'remove' } : { kind: 'keep' })
                }
              />

              {photoError !== null && (
                <div className="mt-3 flex flex-col gap-2">
                  <Notice tone="warning" title="The category was saved, but not the photo">
                    Save again to retry, or remove the photo to finish without one.
                  </Notice>
                  <ApiErrorAlert error={photoError} />
                </div>
              )}
            </Card>

            <Card>
              <SectionTitle>Status</SectionTitle>

              <Form.Item
                name="isAccessory"
                label="Accessory"
                valuePropName="checked"
                extra="Ties, cufflinks, shoes — things that can be rented on their own, not only in a package."
              >
                <Switch size="small" />
              </Form.Item>

              {isEdit && (
                <Form.Item name="isActive" label="Active" valuePropName="checked" className="mb-0">
                  <Switch size="small" />
                </Form.Item>
              )}
            </Card>

            <Card>
              <div className="flex flex-col gap-2">
                <Button
                  variant="primary"
                  icon="icon icon-check"
                  data-enter-key-commit
                  shortcut="save"
                  loading={save.isPending}
                  disabled={save.isPending}
                  onClick={() => form.submit()}
                >
                  {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create category'}
                </Button>

                <Button
                  variant="secondary"
                  disabled={save.isPending}
                  onClick={() => {
                    // A category created by a Save whose photo failed is real, so the list must
                    // show it even though nothing more is written here.
                    if (existing && !category) {
                      void queryClient.invalidateQueries({ queryKey: itemCategoriesKey });
                    }

                    onClose();
                  }}
                >
                  {photoError !== null ? 'Finish without the photo' : 'Cancel'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </Form>
    </>
  );
}

/**
 * The template's dashed upload card, holding one picture. Choosing stages the file; nothing is sent
 * until the page saves.
 */
function PhotoPicker({
  picture,
  blocked,
  onPick,
  onRemove,
}: {
  picture: string | null;
  blocked: string | undefined;
  onPick: (file: File) => void;
  onRemove: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [refused, setRefused] = useState<string | null>(null);

  const choose = (file: File | undefined) => {
    if (!file) {
      return;
    }

    if (!isPhoto(file)) {
      setRefused(`${file.name} is not a photograph. Use a JPEG, PNG, WebP or HEIC picture.`);

      return;
    }

    setRefused(null);
    onPick(file);
  };

  const fileInput = (
    <input
      ref={input}
      type="file"
      accept="image/jpeg,image/png,image/webp,image/heic"
      className="hidden"
      onChange={(event) => {
        choose(event.target.files?.[0]);
        // Cleared so choosing the same file again after removing it still fires a change.
        event.target.value = '';
      }}
    />
  );

  return (
    <div className="flex flex-col gap-3">
      {picture ? (
        <div className="flex flex-col gap-2">
          {/* 4:5, the shape of a sherwani on a hanger — the shape every card will crop to. */}
          <img
            src={picture}
            alt="The category's photograph"
            className="aspect-[4/5] w-full rounded-md border border-border-color object-cover"
          />
          <div className="flex gap-2">
            <Button
              variant="secondary"
              icon="icon icon-image-plus"
              disabled={blocked !== undefined}
              onClick={() => input.current?.click()}
            >
              Change
            </Button>
            <Button
              variant="secondary"
              icon="icon icon-image-minus"
              disabled={blocked !== undefined}
              onClick={onRemove}
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={blocked !== undefined}
          onClick={() => input.current?.click()}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            choose(event.dataTransfer.files[0]);
          }}
          className="flex aspect-[4/5] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border-color p-6 text-center hover:border-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="ph-duotone ph-cloud-arrow-up text-default text-3xl" />
          <span className="text-default text-[length:var(--scale-font-size)]">
            Choose or drop a photo
          </span>
        </button>
      )}

      {fileInput}

      <p className="text-default mb-0 text-[length:var(--scale-field-hint)]">
        {blocked ??
          'One photograph. It shows on the category and on any design here that has none of its own yet.'}
      </p>

      {refused && <Notice tone="warning" title={refused} />}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="mb-3 text-sm font-semibold text-gray-900">{children}</h2>;
}

/** The cap on variant-forming axes — Shopify's, and a merchandising limit rather than a technical one. */
const maxVariantAxes = 3;

/**
 * Which attributes apply to this category, and how.
 *
 * The section exists to make one distinction visible: an attribute assigned *here* and one
 * inherited from an ancestor look identical in their effect and are completely different to edit.
 * The server marks the second with a null `assignmentId`, and this renders it read-only with the
 * ancestor named — otherwise the only way to learn that Ethnic Wear owns the flag is to change it
 * here and watch nothing happen.
 *
 * Its controls write immediately rather than on the page's Save: each is its own endpoint, and the
 * category they hang off already exists.
 */
function CategoryAttributesSection({
  session,
  category,
}: {
  session: Session;
  category: ItemCategory;
}) {
  const [picked, setPicked] = useState<number | null>(null);

  const assigned = useCategoryAttributes(category.id);
  // The pool to assign from. Inactive ones are left out: assigning a retired attribute is not
  // something anyone means to do, and the attribute screen is where it would be revived.
  const catalogue = useItemAttributes(session.branch?.id);

  const rows = assigned.data ?? [];

  /*
    The prefix, not this one category's key. Assignments inherit down the tree, so changing one
    here changes what every descendant resolves — and `categoryAttributesKey` is the prefix of all
    of their query keys, so invalidating it reaches them. Invalidating only `{ categoryId }` would
    leave a child opened afterwards showing the ancestor's previous flags.

    The category list itself is untouched by an assignment, so it is deliberately not invalidated.
  */
  const invalidates = [categoryAttributesKey];

  const assign = useApiMutation<CategoryAttribute[], number>({
    mutationFn: (attributeId) =>
      apiFetch<CategoryAttribute[]>(`/api/item-category/${category.id}/attributes`, {
        method: 'POST',
        body: { attributeId },
      }),
    success: 'Attribute added.',
    invalidates,
    onSuccess: () => setPicked(null),
  });

  const update = useApiMutation<
    CategoryAttribute[],
    { row: CategoryAttribute; isRequired: boolean; isVariantForming: boolean }
  >({
    mutationFn: ({ row, isRequired, isVariantForming }) =>
      apiFetch<CategoryAttribute[]>(
        `/api/item-category/${category.id}/attributes/${row.assignmentId}`,
        { method: 'PUT', body: { isRequired, isVariantForming, sortOrder: row.sortOrder } },
      ),
    invalidates,
  });

  const remove = useApiMutation<CategoryAttribute[], CategoryAttribute>({
    mutationFn: (row) =>
      apiFetch<CategoryAttribute[]>(
        `/api/item-category/${category.id}/attributes/${row.assignmentId}`,
        { method: 'DELETE' },
      ),
    success: (_result, row) => `${row.attributeName} removed.`,
    invalidates,
  });

  const axesUsed = rows.filter((row) => row.isVariantForming).length;
  const assignedIds = new Set(rows.map((row) => row.attributeId));

  const options = (catalogue.data ?? [])
    .filter((attribute) => !assignedIds.has(attribute.id))
    .map((attribute) => ({
      label: `${attribute.name} (${attribute.code})`,
      value: attribute.id,
    }));

  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-900">Attributes</h2>
      <p className="mt-0.5 mb-3 text-[12px] text-default">
        What items in this category vary along. Sub-categories inherit these unless they set their
        own.{' '}
        <span className="font-mono-nums">
          {axesUsed}/{maxVariantAxes}
        </span>{' '}
        variant axes used.
      </p>

      {assigned.isError && <ApiErrorAlert error={assigned.error} />}

      {rows.length > 0 && (
        <ul className="mb-3 divide-y divide-border-color rounded-md border border-border-color ps-0">
          {rows.map((row) => (
            <li key={`${row.attributeId}`} className="list-none px-3 py-2">
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-[13px] font-medium text-gray-900">
                      {row.attributeName}
                    </span>
                    {row.isInherited && (
                      <StatusBadge tone="neutral">
                        from {row.inheritedFromCategoryName ?? 'an ancestor'}
                      </StatusBadge>
                    )}
                    {row.isVariantForming && <StatusBadge tone="brand">Variant axis</StatusBadge>}
                    {row.isRequired && <StatusBadge tone="warning">Required</StatusBadge>}
                  </div>
                  <span className="font-mono-nums text-[11px] text-default">
                    {row.attributeCode} · {humanizeEnum(row.dataType)}
                    {row.dataType === 'List' && ` · ${row.valueCount} values`}
                  </span>
                </div>

                {!row.isInherited && (
                  <WriteAction session={session} required="ItemAttribute:Edit">
                    {(disabled) => (
                      <ConfirmButton
                        icon="icon icon-trash-2"
                        label={`Remove ${row.attributeName}`}
                        title="Remove this attribute?"
                        description="Items here stop varying along it."
                        confirmText="Remove"
                        disabled={disabled}
                        pending={remove.isPending}
                        onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
                      />
                    )}
                  </WriteAction>
                )}
              </div>

              {/*
                The flags are only editable where the assignment lives. An inherited row shows its
                state and says where to go and change it, rather than offering a control that the
                server would refuse.
              */}
              {row.isInherited ? (
                <p className="mt-1 text-[11px] text-default">
                  Set on {row.inheritedFromCategoryName ?? 'an ancestor'}. Add it here to override.
                </p>
              ) : (
                <div className="mt-2 flex flex-wrap items-center gap-5">
                  <label className="inline-flex cursor-pointer items-center gap-2 text-[12px] text-default">
                    <Switch
                      size="small"
                      checked={row.isRequired}
                      disabled={remove.isPending || update.isPending}
                      onChange={(checked) =>
                        void update
                          .mutateAsync({
                            row,
                            isRequired: checked,
                            isVariantForming: row.isVariantForming,
                          })
                          .catch(() => undefined)
                      }
                    />
                    Required
                  </label>

                  <label className="inline-flex cursor-pointer items-center gap-2 text-[12px] text-default">
                    <Switch
                      size="small"
                      checked={row.isVariantForming}
                      // Only a list attribute can be an axis — a variant is a combination of chosen
                      // values, and a free number has nothing to choose from. The cap applies only
                      // to switching one *on*.
                      disabled={
                        row.dataType !== 'List' ||
                        remove.isPending ||
                        update.isPending ||
                        (!row.isVariantForming && axesUsed >= maxVariantAxes)
                      }
                      onChange={(checked) =>
                        void update
                          .mutateAsync({
                            row,
                            isRequired: row.isRequired,
                            isVariantForming: checked,
                          })
                          .catch(() => undefined)
                      }
                    />
                    Forms variants
                  </label>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <WriteAction session={session} required="ItemAttribute:Edit">
        {(disabled) => (
          <div className="flex items-center gap-2">
            <Select
              className="flex-1"
              placeholder={
                options.length === 0 ? 'Every attribute is already here' : 'Add an attribute…'
              }
              options={options}
              value={picked}
              disabled={disabled || options.length === 0}
              loading={catalogue.isLoading}
              showSearch
              optionFilterProp="label"
              onChange={setPicked}
            />

            <Button
              variant="primary"
              icon="icon icon-plus"
              loading={assign.isPending}
              disabled={disabled || picked === null || assign.isPending}
              onClick={() =>
                picked !== null && void assign.mutateAsync(picked).catch(() => undefined)
              }
            >
              Add
            </Button>
          </div>
        )}
      </WriteAction>
    </div>
  );
}
